import { HttpParams } from '@angular/common/http'
import { Component, Input, OnDestroy, OnInit, WritableSignal } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { DataMap } from '@grapecity/wijmo.grid'

import { AutoSizeDirective, BaseComponent, FeatureType, Grid, ListItem, ModalSize, QualityControlPipe, VpJob } from '../../../shared'
import { ActionToolbarButtonsComponent } from '../../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { PipeModalComponent } from './pipe-modal.component'

@Component({
    selector: 'app-quality-control-pipe',
    templateUrl: './pipe.component.html',
    standalone: true,
    imports: [
        FormsModule,
        WjGridModule,
        WjGridFilterModule,
        WjInputModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        AutoSizeDirective,
    ],
})
export class PipeComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() selectedJob!: WritableSignal<string>
    @Input() allJobs: VpJob[] = []

    grid: Grid<QualityControlPipe>
    aggregateVendors: any[] = []
    allVendors: any[] = []
    vendorMap!: DataMap
    trenchZoneVendorMap!: DataMap

    constructor() {
        super()
        this.grid = new Grid<QualityControlPipe>(this.services)
            .setupModalEditing(
                this.services.data.qualityControlPipes,
                'Pipe Quality Control',
                PipeModalComponent,
                () => new QualityControlPipe(),
                this,
                ModalSize.large,
            )
            .filterBy(item => [item.typeOfPipe, item.processForPipe])
            .withColumnFilters()
            .isReadOnly(true)
            .wrapColumnHeaders()

        this.grid.itemAdded.on(this.handleItemAdded.bind(this))
        this.grid.modified.on(this.refreshData.bind(this))
    }

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.FieldToolsPlansAndSpecsEdit])
    }

    get selectedJobName(): string {
        const jobId = this.selectedJob()
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId)
        return job ? `${job.id} ${job.name}` : jobId
    }

    ngOnInit() {
        this.loadVendors()
        this.loadAllVendors()
    }

    loadVendors() {
        this.services.data.get<any[]>('api/Vendor/GetForAggregate').subscribe({
            next: (result: any[]) => {
                this.aggregateVendors = result || []
                this.buildTrenchZoneVendorMap()
            },
            error: () => {
                this.services.notification.error('Failed to load vendors')
            },
        })
    }

    loadAllVendors() {
        this.services.data.get<any[]>('api/Vendor').subscribe({
            next: (result: any[]) => {
                this.allVendors = result || []
                this.buildVendorMap()
            },
            error: () => {
                this.services.notification.error('Failed to load vendors')
            },
        })
    }

    buildVendorMap() {
        const vendorOptions: ListItem<number | null>[] = []
        vendorOptions.push(new ListItem<number | null>(null, ''))
        this.allVendors.forEach(x => {
            vendorOptions.push(new ListItem<number | null>(x.id, x.name))
        })
        this.vendorMap = new DataMap(vendorOptions, 'value', 'display')
    }

    buildTrenchZoneVendorMap() {
        const options: ListItem<number | null>[] = []
        options.push(new ListItem<number | null>(null, ''))
        this.aggregateVendors.forEach(x => {
            options.push(new ListItem<number | null>(x.id, x.name))
        })
        this.trenchZoneVendorMap = new DataMap(options, 'value', 'display')
    }

    ngOnDestroy(): void {
        this.grid.itemAdded.off(this.handleItemAdded.bind(this))
        this.grid.modified.off(this.refreshData.bind(this))
        this.grid.destroy()
    }

    loadPipeData(jobId: string) {
        if (!jobId || jobId === '') {
            this.grid.items = []
            return
        }

        const params = new HttpParams().set('jobId', jobId)
        this.services.data.get<QualityControlPipe[]>('api/QualityControlPipe/GetByJob', params).subscribe({
            next: (result: QualityControlPipe[]) => {
                this.grid.items = result || []
            },
            error: () => {
                this.services.notification.error('Failed to load pipe data')
                this.grid.items = []
            },
        })
    }

    handleItemAdded(item: QualityControlPipe) {
        item.job = this.selectedJob()
    }

    refreshData() {
        const jobId = this.selectedJob()
        if (jobId && jobId !== '') {
            this.loadPipeData(jobId)
        }
    }

    onGridInitialized() {
        // Grid initialized
    }
}
